import { NestedTreeControl } from '@angular/cdk/tree'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTreeNestedDataSource } from '@angular/material/tree'
import { ActivatedRoute, Router } from '@angular/router'
import * as moment from 'moment'
import { Subscription } from 'rxjs'
import { AuthorizationService } from '../../../../access/shared/authorization/authorization.service'
import { Permissions } from '../../../../access/shared/authorization/permissions'
import { NotificationService } from '../../../../notification/shared/notification.service'
import { Client } from '../../../shared/client/client'
import { ClientService } from '../../../shared/client/client.service'
import { Commande, CommandeDetails, ListLigneCommandeInv } from '../../shared/commande'
import { CommandeService } from '../../shared/commande.service'
import { Article, Inventaire, Lingerie } from '../../shared/inventaire'
import { BesoinDispatchValidateDialogComponent } from './besoin-dispatch-validate-dialog/besoin-dispatch-validate-dialog.component'
import { UntypedFormControl } from '@angular/forms'

interface LigneCommandeNode {
  id?: string
  quantite: number
  article: Article
  qte_stock: number
  preCommande?: number
  inventaires?: any
  children?: LigneCommandeNode[]
}

interface InventaireNode {
  id: number
  commande: Commande
  lingerie: Lingerie
  statut: any
  date_modification: Date
  children?: InventaireNode[]
}

@Component({
  selector: 'app-besoin-dispatch',
  templateUrl: './besoin-dispatch.component.html',
  styleUrls: ['./besoin-dispatch.component.scss']
})
export class BesoinDispatchComponent implements OnInit, OnDestroy {

  subClient: Subscription
  currentClient: Client
  commandeId: string
  loading: boolean = false
  commandeDetails: CommandeDetails
  selected: string = "reception"
  isReceptionUpdate: boolean = false
  isSaving: boolean = false
  readonly: boolean = true
  disabledUpdate: boolean = false

  treeControl = new NestedTreeControl<LigneCommandeNode>(node => node.children)
  treeControl2 = new NestedTreeControl<InventaireNode>(node => node.children)
  dataSourceR = new MatTreeNestedDataSource<ListLigneCommandeInv>()
  dataSourceL = new MatTreeNestedDataSource<Inventaire>()

  commentaire = new UntypedFormControl('', [])

  constructor(
    private snackBar: MatSnackBar,
    private clientService: ClientService,
    private route: ActivatedRoute,
    private commandeService: CommandeService,
    private router: Router,
    private authService: AuthorizationService,
    private notificationService: NotificationService,
    private dialog: MatDialog
  ) {
    moment().locale('fr')
  }

  ngOnInit(): void {
    this.subClient = this.clientService.getCurrentClient().subscribe(client => {
      this.currentClient = client
      this.route.queryParams.subscribe(params => {
        this.commandeId = params.id
        this.getCommandeDetails(this.commandeId)
        this.loading = false
      })
    })
  }

  ngOnDestroy() {
    this.snackBar.dismiss()
    if (this.subClient) {
      this.subClient.unsubscribe()
    }
  }

  getCommandeDetails(commandeId: string) {
    this.loading = true
    this.commandeService.getCommandeDetails(commandeId).subscribe(async data => {
      this.commandeDetails = data
      if (this.authService.hasPermission(Permissions.ROLE_HEBERGEMENT_LINGERE)) {
        this.refuse()
      }
      this.getReadonly()
      this.getIsReceptionUpdate()
      this.getTreeDispatch()
      this.getLivraisonDatas()
    })
  }

  getReadonly() {
    this.commandeService.getLastCommand(this.currentClient.id).subscribe(commande => {
      if (commande) {
        let isFirst = (commande.id == this.commandeId)
        this.readonly = !(this.commandeDetails.statut.code == 'AD' && isFirst)
      } else {
        this.readonly = false
      }
    })
  }

  changeTab(tab) {
    this.selected = tab
  }

  refuse() {
    this.router.navigate(['/escli/linge-hebergement/list'])
  }

  getTreeDispatch() {
    if (this.commandeDetails) {
      this.dataSourceR.data = this.commandeDetails.lod_ligne_commandes
      let inventaires = this.commandeDetails.inventaires
      this.dataSourceR.data.forEach(ligne => {
        let inventaireArticle = []
        inventaires.forEach(inventaire => {
          inventaire.inventaire_lignes.forEach(invLigne => {
            if (invLigne.article.id == ligne.article.id) {
              inventaireArticle.push({ lingerie: inventaire.lingerie, commande: invLigne.qte_commande, ligneId: invLigne.id, qte_dispatch: invLigne.qte_dispatch, dotation: invLigne.dotation, qte_complement: invLigne.qte_complement })
            }
          })
        })
        ligne.inventaires = inventaireArticle
      })
    }
    this.loading = false
  }

  dispatchAuto(article) {
    article.isDispatchAuto = true
    article.inventaires.forEach(value => {
      if (article.quantite == 0) {
        value.qte_dispatch = 0
      } else {
        value.qte_dispatch = Math.floor((this.getQuantiteComparaison(article) * value.commande) / article.quantite)
      }
    })
    if (article.quantite == 0) {
      this.notificationService.notif("Besoin à 0 : les quantités livrées ne peuvent pas être dispatchées par lingerie.", "warn")
    }
    else if (this.getSommeInventaireArticle(article) != this.getQuantiteComparaison(article)) {
      this.notificationService.notif("Les quantités livrées ne permettant pas toujours un dispatch automatique parfait, nous vous invitons à ajuster manuellement, si nécessaire, les quelques quantités qui pourraient être incorrectes.", "warn")
    }
  }

  getSommeInventaireArticle(article) {
    let result = 0
    article.inventaires.forEach(value => {
      result += value.qte_dispatch ?? 0
    })
    return result
  }

  getQuantiteComparaison(article) {
    return Math.min(article.quantite, article.qte_reception ?? article.qte_livree)
  }

  updateInventaire(article, ligne, event) {
    article.isDispatchAuto = false
    if (!article.qte_livree) {
      if (event.target.value > 0) {
        this.notificationService.notif("Quantité saisie supérieure à la quantité livrée", 'warn')
      }
      event.target.value = 0
    }
    else if (+event.target.value > article.qte_livree) {
      event.target.value = article.qte_livree
      this.notificationService.notif("Quantité saisie supérieure à la quantité livrée", 'warn')
    }
    else if (+event.target.value < 0) {
      event.target.value = 0
    }
    let oldValue = ligne.qte_dispatch
    ligne.qte_dispatch = event.target.value !== "" ? +event.target.value : null

    if (this.getSommeInventaireArticle(article) > article.qte_livree) {
      this.notificationService.notif("La répartition dépasse la quantité réceptionnée", 'warn')
      ligne.qte_dispatch = oldValue
      event.target.value = oldValue
    } else if (event.target.value > ligne.commande) {
      this.notificationService.notif("La répartition dépasse le besoin", 'warn')
      ligne.qte_dispatch = oldValue
      event.target.value = oldValue
    }
  }

  saveDispatch() {
    this.saveComplement()
    this.isSaving = true
    this.loading = true
    this.commandeService.saveDispatch(this.commandeId, this.dataSourceR.data).subscribe(value => {
      this.commandeDetails = value
      this.getLivraisonDatas()
      this.selected = 'distribution'
      this.notificationService.notif("La répartition a bien été enregistrée", 'notif')
      this.getReadonly()
      this.isSaving = false
      this.loading = false
    })
  }

  getLivraisonDatas() {
    this.dataSourceL.data = this.commandeDetails.inventaires
  }

  updateComplement(ligne, event) {
    if (!ligne.dotation) {
      if (event.target.value > 0) {
        this.notificationService.notif("Quantité saisie supérieure à la dotation", 'warn')
      }
      event.target.value = 0
    }
    else if (+event.target.value > ligne.dotation) {
      event.target.value = ligne.dotation
      this.notificationService.notif("Quantité saisie supérieure à la dotation", 'warn')
    }
    else if (+event.target.value < 0) {
      event.target.value = 0
    }
    ligne.qte_complement = event.target.value !== "" ? +event.target.value : null
    ligne.qte_livree = ligne.qte_complement + ligne.qte_dispatch
  }

  saveComplement() {
    this.isSaving = true
    this.commandeService.saveComplement(this.commandeId, this.dataSourceR.data).subscribe(value => {
      this.commandeDetails = value
      this.getLivraisonDatas()
      this.isSaving = false
    })
  }

  updateReception(ligne, event) {
    if (+event.target.value < 0) {
      event.target.value = 0
    }
    ligne.qte_reception = event.target.value !== "" ? +event.target.value : null
  }

  saveReception() {
    if (!this.commandeDetails.lod_ligne_commandes.every(value => value.qte_reception != null)) {
      this.notificationService.notif("il faut renseigner toutes les données réellement reçues pour valider la réception.", 'error')
    } else {
      this.isSaving = true
      const dialogRef = this.dialog.open(BesoinDispatchValidateDialogComponent)
      dialogRef.beforeClosed().subscribe(result => {
        if (result) {
          let infos = {
            "lignes": this.commandeDetails.lod_ligne_commandes,
            "commentaire": this.commentaire.value
          }
          this.commandeService.saveBonLivraison(this.commandeId, infos).subscribe(result => {
            this.isSaving = false
            this.disabledUpdate = true
            this.commentaire.disable()
          }, error => {
            this.isSaving = false
          })
        } else {
          this.isSaving = false
        }
      })
    }
  }

  modify() {
    this.isReceptionUpdate = true
  }

  getIsReceptionUpdate() {
    if (this.commandeDetails.lod_ligne_commandes.some(value => value.qte_reception != null)) {
      this.commentaire.setValue(this.commandeDetails.commentaire)
      this.commentaire.disable()
      this.isReceptionUpdate = true
      this.disabledUpdate = true
    }
  }
}
